import type { Activity } from "@/types/data.types";
import type { SportsFilter } from "@/types/filters.types";
import { nextInstantIso } from "./date.utils";

function includesCI(value: string, term: string) {
  return value.toLowerCase().includes(term.toLowerCase());
}

export function matchesSearch(
  a: Pick<Activity, "name" | "sport_type" | "type">,
  term: string,
) {
  return (
    includesCI(a.name ?? "", term) ||
    includesCI(a.sport_type ?? "", term) ||
    includesCI(a.type ?? "", term)
  );
}

export function applyBasicFilters(
  rows: Activity[],
  q: {
    search?: string;
    sports?: SportsFilter;
    from?: string;
    to?: string;
    limit?: number;
  },
): Activity[] {
  const fromMs = q.from ? new Date(q.from).getTime() : undefined;
  const toExclusiveMs = q.to
    ? new Date(nextInstantIso(q.to)).getTime()
    : undefined;

  const term = q.search?.trim();

  return rows
    .filter((a) => {
      if (!a.start_date) return false;

      const t = new Date(a.start_date).getTime();
      if (!Number.isFinite(t)) return false;

      if (fromMs !== undefined && t < fromMs) return false;
      if (toExclusiveMs !== undefined && t >= toExclusiveMs) return false;

      return true;
    })
    .filter((a) => {
      if (!term) return true;
      return matchesSearch(
        {
          name: a.name,
          sport_type: a.sport_type,
          type: a.type,
        },
        term,
      );
    })
    .filter((a) => {
      const f = q.sports;
      if (!f || f.mode === "all") return true;
      if (!a.sport_type) return false;
      if (f.mode === "include") {
        if (f.sports.length === 0) return false;
        return f.sports.includes(a.sport_type);
      }
      if (f.sports.length === 0) return true;
      return !f.sports.includes(a.sport_type);
    })
    .slice(0, Math.min(q.limit || 10_000))
    .sort((a, b) => {
      const ad = a.start_date ? new Date(a.start_date).getTime() : null;
      const bd = b.start_date ? new Date(b.start_date).getTime() : null;

      if (ad === null && bd === null) return 0;
      if (ad === null) return 1;
      if (bd === null) return -1;
      return bd - ad; // newest first
    });
}
